import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { LoadFailure } from '../LoadFailure.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { agents } from './agentsClient.js';
import {
  AGENTS_APPROVE_KEY,
  agentAuthority,
  agentAuthoritySentence,
  authorityOpen,
} from './consentLogic.js';

/** The server's minimum (`decisionSchema.reason`): a decision nobody can review later is not one. */
export const MIN_DECISION_REASON = 3;

/**
 * THE APPROVALS INBOX: every step any agent in the organization is waiting on.
 *
 * Seeing what is waiting is `agents:read` (the page's key); deciding it is
 * `agents:approve`, so the controls render only for a holder and everyone else
 * is told why. A reason is required — the server refuses a decision without
 * one, and it is stored against the exact step.
 */
export function AgentApprovals() {
  const queryClient = useQueryClient();
  const approve = agentAuthority(usePermissions(), AGENTS_APPROVE_KEY);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);

  const pending = useQuery({
    queryKey: ['agents', 'approvals'],
    queryFn: () => agents.approvals(),
  });

  const decide = useMutation({
    mutationFn: (input: { approvalId: string; decision: 'GRANTED' | 'DENIED'; reason: string }) =>
      agents.decideApproval(input.approvalId, input.decision, input.reason),
    onSuccess: (_run, input) => {
      setError(null);
      setReasons((current) => ({ ...current, [input.approvalId]: '' }));
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => setError(err),
  });

  const items = pending.data ?? [];
  const gateSentence = agentAuthoritySentence(approve, 'decide what an agent is waiting on');

  return (
    <section className="rn-card" data-testid="agent-approvals">
      <h2 className="rn-card-title">Waiting for a decision</h2>
      <ListState
        phase={listPhase(pending, items.length)}
        surface="table"
        rows={2}
        failedMessage="We could not load what agents are waiting on. This is not a statement that nothing is waiting."
        onRetry={() => void pending.refetch()}
        empty={<p className="rn-muted">No agent is waiting on a decision.</p>}
      >
        <div className="rn-col" style={{ gap: 'var(--space-12)' }}>
          {gateSentence ? (
            <p className="rn-muted" data-approve-gate={approve.kind}>
              {gateSentence}
            </p>
          ) : null}
          {error ? (
            <LoadFailure
              error={error}
              message="That decision was not recorded. Nothing has changed."
              refusedHeading="You may not decide this"
            />
          ) : null}
          <ul className="rn-list">
            {items.map((approval) => {
              const reason = (reasons[approval.id] ?? '').trim();
              const send = (decision: 'GRANTED' | 'DENIED') =>
                decide.mutate({ approvalId: approval.id, decision, reason });
              return (
                <li key={approval.id} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                  <span className="rn-strong">
                    {approval.reason ??
                      'An agent is waiting for a person to approve its next step.'}
                  </span>
                  <a className="rn-link" href={`/agents/runs/${approval.runId}`}>
                    Open the run to see what it would do
                  </a>
                  {authorityOpen(approve) ? (
                    <>
                      <Input
                        label="Why"
                        hint="Recorded with your decision against this exact step."
                        value={reasons[approval.id] ?? ''}
                        maxLength={1000}
                        onChange={(e) =>
                          setReasons((current) => ({ ...current, [approval.id]: e.target.value }))
                        }
                      />
                      <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
                        <Button
                          variant="primary"
                          disabled={reason.length < MIN_DECISION_REASON || decide.isPending}
                          onClick={() => send('GRANTED')}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={reason.length < MIN_DECISION_REASON || decide.isPending}
                          onClick={() => send('DENIED')}
                        >
                          Deny — the run stops
                        </Button>
                      </div>
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      </ListState>
    </section>
  );
}
